import React, { useCallback, useState } from 'react';
import {
  BrandCampaignItem,
  BrandCampaignItemPatchDTO,
  BrandMgSupplierCampaignItem,
  BrandMgSupplierCampaignItemLookup,
  BrandMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
} from '@retail/campaign/types';
import {
  addCampaignItemsSchema,
  AddCampaignItemsSchema,
  brandCampaignItemSchema,
  BrandCampaignItemSchema,
  useGetAddBrandCampaignItemsColumns,
} from '@retail/campaign/components';
import { useAppTFunction } from '@retail/app/i18n';
import { EditCampaignItemsDialog } from './EditCampaignItemsDialog';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { useLookupCampaignItems } from '@retail/campaign/data-access';
import { useSearchParams } from 'react-router';

interface Props {
  campaignId: string;
  campaignItems: BrandCampaignItem[];
  onClose: () => void;
}

function BrandEditCampaignItemsDialogComponent({ campaignId, campaignItems, onClose }: Props) {
  const { appTenant } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();
  const getCampaignItemColumns = useGetAddBrandCampaignItemsColumns({
    externalSourceType: appTenant.externalItemSource,
    showPackageDiscounts: selectedBrand.wholesaler,
  });

  const [, setURLSearchParams] = useSearchParams();
  const [lookupItemErrors, setLookupItemErrors] = useState<BrandMgSupplierCampaignItemLookupDTO[]>(
    []
  );
  const [lookupItems, setLookupItems] = useState<BrandMgSupplierCampaignItem[]>([]);

  const { mutateAsync: handleLookupItems, isPending: isPendingLookupItems } =
    useLookupCampaignItems<BrandMgSupplierCampaignItemLookupDTO>();

  const performItemsLookup = useCallback(
    (lookupSearchItems: MgSupplierItemLookupDTO[]) => {
      handleLookupItems({ body: lookupSearchItems }).then((response) => {
        const itemsWithErrors = response.filter((item) => item.errorCode);
        if (itemsWithErrors.length) {
          setLookupItemErrors(itemsWithErrors);
          return;
        }

        const items = response
          .map(BrandMgSupplierCampaignItemLookup.fromDto)
          .flatMap((lookup) => lookup.items ?? []);
        setLookupItems(items);
        setURLSearchParams();
      });
    },
    [handleLookupItems, setURLSearchParams]
  );

  return (
    <EditCampaignItemsDialog<
      BrandCampaignItem,
      BrandMgSupplierCampaignItem,
      BrandCampaignItemPatchDTO,
      BrandMgSupplierCampaignItemLookupDTO,
      BrandCampaignItemSchema
    >
      campaignId={campaignId}
      campaignItems={campaignItems}
      open
      onClose={onClose}
      type="brand"
      getCampaignItemsColumns={getCampaignItemColumns}
      transformToPutDTO={(item, lookupItem, campaignItem) => ({
        id: campaignItem.id,
        campaignUnit: item.campaignUnit,
        campaignSalesPrice: item.campaignSalesPrice ?? null,
        campaignPurchasePrice: item.campaignPurchasePrice ?? undefined,
        isSalesPriceOverridable: !item.fixedSalesPrice,
      })}
      isPendingLookupItems={isPendingLookupItems}
      performItemsLookup={performItemsLookup}
      lookupItemErrors={lookupItemErrors}
      lookupItems={lookupItems}
      setLookupItems={setLookupItems}
    />
  );
}

export function BrandEditCampaignItemsDialog(props: Props) {
  const t = useAppTFunction();
  const formItems: AddCampaignItemsSchema<BrandCampaignItemSchema>['items'] =
    props.campaignItems.reduce(
      (prev, item) => ({
        ...prev,
        [item.mgSupplierItemId + '']:
          // @ts-ignore
          {
            campaignUnit: item.campaignUnit,
            campaignSalesPrice: item.getSalesPriceForCampaignUnit()?.priceWithVat || '',
            campaignPurchasePrice: item.getPurchasePriceForCampaignUnit()?.priceWithoutVat || '',
            fixedSalesPrice: !item.isSalesPriceOverridable,
          } as BrandCampaignItemSchema,
      }),
      {}
    );

  const formMethods = useForm<AddCampaignItemsSchema<BrandCampaignItemSchema>>({
    resolver: yupResolver(addCampaignItemsSchema(brandCampaignItemSchema(t))),
    shouldUnregister: false,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      items: formItems,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <BrandEditCampaignItemsDialogComponent {...props} />
    </FormProvider>
  );
}
